import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { uniqBy } from 'lodash';

import { CallStatus } from '../../enums';
import { PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import { Company, CompanyLogo } from '../../models/company';
import {
  AvailableIndexesResponse,
  BalanceSheet,
  BaseRatiosParamType,
  BaseStatementSearchParamType,
  CashFlow,
  CompanyLogoAndNameResponse,
  DividendPayerData,
  Earning,
  EpicMoversParamType,
  EpicMoversResponse,
  ETF,
  ETFDetailParams,
  ETFDetailResponse,
  ETFParams,
  GapperCountResponse,
  GapperParamType,
  GapperResponse,
  HighGrowthCompanyData,
  Income,
  IPO,
  LiquidityAndSolvencyRatiosResponse,
  NewStockData,
  PageableFilterRequest,
  PopularStock,
  PopularStockParams,
  ProfitabilityRatiosResponse,
  RatiosResponse,
  Sector,
  SectorDetail,
  Split,
  StatementApiResponse,
  StocksSymbolSet,
  TopGainerAndLoserData,
  TopGainerAndLoserPayload,
  ValuationRatiosResponse,
  Week52HighLowParamType,
  Week52HighLowResponse,
  Week52HighLowTopListResponse,
} from '../../models/fundamental/types';
import { DEFAULT_PAGE_RESPONSE } from '../../models/pageable';
import { defaultLogoBase64 } from '../../util/assets/logos';
import { RegExStrings } from '../../util/DataHelpers';
import { NullableNumber, NullableString } from '../../util/types';
import { clearErrorsLog, ErrorPayloadFull } from '../common-actions';

import {
  DEFAULT_EPIC_MOVERS_DATA,
  DEFAULT_RATIOS_AND_FILTER_PARAMS,
  DEFAULT_SEARCH_STOCK_LIMIT,
  INITIAL_STATE,
  marketIndexesArray,
  NAMES_SECTORS_CONTAINING_IMAGES,
  NO_MORE_PAGES_TO_READ,
} from './constants';
import {
  parseETFName,
  populateEarningCalendarLabels,
  processCallCache,
  setFundamentalCallStatus,
  updateCompanyAndLogoCache,
} from './helpers';
import {
  FundamentalCall,
  FundamentalState,
  GetSectorsResponse,
  SearchEarningsPayload,
  SearchStockPayload,
} from './types';

const fundamentalSlice = createSlice({
  name: 'fundamental',
  initialState: INITIAL_STATE,
  reducers: {
    searchStockByParams(state, action: PayloadAction<SearchStockPayload>) {
      const { stockQuery, limit: stateLimit } = state;
      const { query, limit } = action.payload;
      state.stockQuery = query;

      if (!query || query !== stockQuery) {
        state.limit = DEFAULT_SEARCH_STOCK_LIMIT;
        state.stockResults = [];
      } else {
        state.limit = limit;
      }

      setFundamentalCallStatus(FundamentalCall.searchStocks, !query ? CallStatus.READY : CallStatus.PENDING, state);
    },
    searchStock(state, action: PayloadAction<string>) {
      state.stockQuery = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.PENDING, state);
    },
    stockSearchCompleted(state, action) {
      state.stockResults = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.READY, state);
    },
    stockSearchFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.ERROR, state, status);
    },
    clearStockSearch(state) {
      state.stockQuery = '';
      state.stockResults = [];
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.INITIAL, state);
    },
    stockSearchReset(state, action: PayloadAction<boolean>) {
      state.stockResults = [];
      if (action.payload) {
        setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.INITIAL, state);
      }
    },
    loadStock(state, action: PayloadAction<{ symbol: string, size?: NullableNumber }>) {
      state.symbol = action.payload.symbol;
    },
    searchEarnings(state, action: PayloadAction<SearchEarningsPayload>) {
      setFundamentalCallStatus(FundamentalCall.searchEarnings, CallStatus.PENDING, state);
    },
    searchEarningsCompleted(state, action: PayloadAction<PagedResponse<Earning>>) {
      const { number: page, content } = populateEarningCalendarLabels(action.payload) ?? {};
      state.earningsSearchResponse = page === 0 ? populateEarningCalendarLabels(action.payload) : {
        ...populateEarningCalendarLabels(action.payload),
        content: state.earningsSearchResponse.content.concat(content),
      };
      setFundamentalCallStatus(FundamentalCall.searchEarnings, CallStatus.READY, state);
    },
    searchEarningsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchEarnings, CallStatus.ERROR, state, status);
    },
    keyStatsFailed(state, action) {
      state.keyStats = [];
      state.error = action.payload;
    },
    keyStatsCompleted(state, action) {
      state.keyStats = action.payload;
    },
    searchCompany(state, action: PayloadAction<{symbol: string, limit?: number}>) {},
    searchCompanyCompleted(state: FundamentalState, action: PayloadAction<Company[]>) {
      state.companies = action.payload;
    },
    searchCompanyFailed(state, action) {
      state.companies = [];
      state.error = action.payload;
    },
    getCompany(state, action: PayloadAction<string>) {},
    getCompanyCompleted(state: FundamentalState, action: PayloadAction<Company[]>) {
      state.companies = [ ...state.companies, ...action.payload ];
    },
    getCompanyFailed(state, action) {
      state.companies = [];
      state.error = action.payload;
    },
    // TODO: Delete after merging ART-1918 and use getCompanyLogoAndName for both mobile and web
    getCompanyLogo(state, action: PayloadAction<string>) {},
    getCompanyLogoCompleted(state, action: PayloadAction<CompanyLogo[]>) {
      action.payload?.forEach(item => {
        state.logosBase64[item.symbol] = item.base64Data;
      });
    },
    getCompanyLogoFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.ERROR, state, status);
    },
    getCompanyLogoAndName(state, action: PayloadAction<string>) {
      if (processCallCache(action.payload, 'getCompanyLogoAndName') !== '') {
        setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.PENDING, state);
      }
    },
    getCompanyLogoAndNameCompleted(state, action: PayloadAction<CompanyLogoAndNameResponse[]>) {
      action.payload?.forEach(item => {
        state.companyLogoAndName[item.symbol] = {
          url: item.url,
          logo: item.base64Data ?? defaultLogoBase64,
          name: item.name,
        };
      });
      setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.READY, state);
    },
    getCompanyLogoAndNameFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.ERROR, state, status);
    },
    getPopularStocks(state, action: PayloadAction<PopularStockParams | undefined>) {
      setFundamentalCallStatus(FundamentalCall.getPopularStocks, CallStatus.PENDING, state);
    },
    getPopularStocksCompleted(state, action: PayloadAction<PopularStock[]>) {
      const stocks = action.payload;
      state.popularStocks = stocks.map(
        ({ symbol, popularity, symbolUniverseSymbol }) => ({ symbol, popularity, symbolUniverseSymbol }),
      );
      setFundamentalCallStatus(FundamentalCall.getPopularStocks, CallStatus.READY, state);
      updateCompanyAndLogoCache(stocks, state);
    },
    getPopularStocksFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getPopularStocks, CallStatus.ERROR, state, status);
    },
    getNewStocks(state, action: PayloadAction<StocksSymbolSet | undefined>) {
      setFundamentalCallStatus(FundamentalCall.getNewStocks, CallStatus.PENDING, state);
    },
    getNewStocksCompleted(state, action: PayloadAction<NewStockData[]>) {
      const stocks = action.payload;
      state.newStocks = stocks;
      setFundamentalCallStatus(FundamentalCall.getNewStocks, CallStatus.READY, state);
      updateCompanyAndLogoCache(stocks, state);
    },
    getNewStocksFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getNewStocks, CallStatus.ERROR, state, status);
    },
    getDividendPayers(state, action: PayloadAction<PageableFilterRequest|undefined>) {
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.PENDING, state);
    },
    getDividendPayersCompleted(state, action: PayloadAction<PagedResponse<DividendPayerData>>) {
      const { content, number } = action.payload;
      const uniqueContent = uniqBy(content, 'symbol');
      state.dividendPayers = number === 0 ? { ...action.payload, content: uniqueContent } : {
        ...action.payload,
        content: state.dividendPayers.content.concat(uniqueContent),
      };
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.READY, state);
      updateCompanyAndLogoCache(content, state);
    },
    getDividendPayersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.ERROR, state, status);
    },
    getHighGrowthCompanies(state, action: PayloadAction<PageableFilterRequest|undefined>) {
      setFundamentalCallStatus(FundamentalCall.getHighGrowthCompanies, CallStatus.PENDING, state);
    },
    getHighGrowthCompaniesCompleted(state, action: PayloadAction<PagedResponse<HighGrowthCompanyData>>) {
      const { content, number } = action.payload;
      const uniqueContent = uniqBy(content, 'symbol');
      state.highGrowthCompanies = number === 0 ? { ...action.payload, content: uniqueContent } : {
        ...action.payload,
        content: state.highGrowthCompanies.content.concat(uniqueContent),
      };
      setFundamentalCallStatus(FundamentalCall.getHighGrowthCompanies, CallStatus.READY, state);
      updateCompanyAndLogoCache(content, state);
    },
    getHighGrowthCompaniesFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getHighGrowthCompanies, CallStatus.ERROR, state, status);
    },
    getSectors(state, action: PayloadAction<undefined>) {
      setFundamentalCallStatus(FundamentalCall.getSectors, CallStatus.PENDING, state);
    },
    getSectorsCompleted(state, action: PayloadAction<GetSectorsResponse[]>) {
      const tempSectors: Sector[] = [];
      action.payload
        .filter(sector => sector.sector !== 'N/A' && sector.sector !== 'NULL')
        .filter(sector => NAMES_SECTORS_CONTAINING_IMAGES.includes(sector.sector))
        .forEach(sec => {
          const {
            id,
            sector,
            symbolUniverseSymbolCount: symbolCount,
            logoBase64,
          } = sec;

          tempSectors.push({ id, sector, symbolCount: symbolCount || 0, logoBase64 });
        });

      state.sectors = tempSectors;
      setFundamentalCallStatus(FundamentalCall.getSectors, CallStatus.READY, state);
    },
    getSectorsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getSectors, CallStatus.ERROR, state, status);
    },
    getSectorDetail(state, action: PayloadAction<number>) {
      setFundamentalCallStatus(FundamentalCall.getSectorDetails, CallStatus.PENDING, state);
    },
    getSectorDetailCompleted(state, action: PayloadAction<SectorDetail>) {
      const response = {
        ...action.payload,
        // This is for adding default logo if no logo is available
        content: action.payload.content?.map(el => (el?.logoBase64 ? el : { ...el, logoBase64: defaultLogoBase64 })),
      };

      if (state.sectorDetails.sectorId === response.sectorId) {
        response.content.map(item => state.sectorDetails.content.push(item));
      } else {
        state.sectorDetails = { ...state.sectorDetails, content: response.content, nextPageToRead: 0 };
      }

      state.sectorDetails.nextPageToRead = state.sectorDetails.content?.length >= response.totalElements!
        ? NO_MORE_PAGES_TO_READ
        : state.sectorDetails.nextPageToRead += 1;
      state.sectorDetails.sector = response.sector;
      state.sectorDetails.sectorId = response.sectorId;
      setFundamentalCallStatus(FundamentalCall.getSectorDetails, CallStatus.READY, state);
      updateCompanyAndLogoCache(response.content, state);
    },
    getSectorDetailFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getSectorDetails, CallStatus.ERROR, state, status);
    },
    getETFs(state, action: PayloadAction<ETFParams>) {
      setFundamentalCallStatus(FundamentalCall.getETFs, CallStatus.PENDING, state);
    },
    getETFsCompleted(state, action: PayloadAction<PagedResponse<ETF>>) {
      const { content, totalElements } = action.payload;
      state.ETFData.totalElements = totalElements;
      if (content.length > 1) {
        const etfs = content.map((etf: ETF) => {
          const { name, ...rest } = etf;
          return { companyName: name && parseETFName(name), ...rest };
        });
        state.ETFData.content = [ ...state.ETFData.content, ...etfs ];
        if (state.ETFData.content.length === state.ETFData.totalElements) {
          state.ETFData.nextPage = -1;
        } else {
          state.ETFData.nextPage += 1;
        }
      }
      setFundamentalCallStatus(FundamentalCall.getETFs, CallStatus.READY, state);
    },
    getETFsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getETFs, CallStatus.ERROR, state, status);
    },
    getETFDetails(state, action: PayloadAction<ETFDetailParams>) {
      setFundamentalCallStatus(FundamentalCall.getETFDetails, CallStatus.PENDING, state);
    },
    getETFDetailsCompleted(state, action: PayloadAction<PagedResponse<ETFDetailResponse>>) {
      const details = action.payload.content;
      const updatedETFs = state.ETFData.content.map(etf1 => {
        const matchingEtf = details.find((etf2: ETFDetailResponse) => etf2.symbol === etf1.symbol);
        if (matchingEtf) {
          return { ...etf1, ...matchingEtf };
        }
        return etf1;
      });

      state.ETFData.content = [ ...updatedETFs ];
      setFundamentalCallStatus(FundamentalCall.getETFDetails, CallStatus.READY, state);
    },
    getETFDetailsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getETFDetails, CallStatus.ERROR, state, status);
    },
    getStatements(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatements, CallStatus.PENDING, state);
    },
    getStatementsCompleted(state, action: PayloadAction<StatementApiResponse>) {
      state.statementsData.balanceSheet = action.payload.balanceSheet;
      state.statementsData.cashFlow = action.payload.cashFlow;
      state.statementsData.income = action.payload.income;
      setFundamentalCallStatus(FundamentalCall.getStatements, CallStatus.READY, state);
    },
    getStatementsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatements, CallStatus.ERROR, state, status);
    },
    getStatementsCashFlow(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatementsCashFlow, CallStatus.PENDING, state);
    },
    getStatementsCashFlowCompleted(state, action: PayloadAction<CashFlow[]>) {
      state.statementsData.cashFlow = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsCashFlow, CallStatus.READY, state);
    },
    getStatementsCashFlowFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsCashFlow, CallStatus.ERROR, state, status);
    },
    getStatementsBalanceSheet(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatementsBalanceSheet, CallStatus.PENDING, state);
    },
    getStatementsBalanceSheetCompleted(state, action: PayloadAction<BalanceSheet[]>) {
      state.statementsData.balanceSheet = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsBalanceSheet, CallStatus.READY, state);
    },
    getStatementsBalanceSheetFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsBalanceSheet, CallStatus.ERROR, state, status);
    },
    getStatementsIncome(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatementsIncome, CallStatus.PENDING, state);
    },
    getStatementsIncomeCompleted(state, action: PayloadAction<Income[]>) {
      state.statementsData.income = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsIncome, CallStatus.READY, state);
    },
    getStatementsIncomeFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsIncome, CallStatus.ERROR, state, status);
    },
    getWeek52HighLow(state, action: PayloadAction<Week52HighLowParamType>) {
      const { page, changeStatus } = action.payload;
      if (changeStatus !== state.week52HighLow.content[0]?.changeStatus) { // remove old page data after each tab change
        state.week52HighLow = { ...DEFAULT_PAGE_RESPONSE };
      }
      state.week52HighLow.number = page ?? 0;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLow, CallStatus.PENDING, state);
    },
    getWeek52HighLowCompleted(state, action: PayloadAction<SimplePagedResponse<Week52HighLowResponse>>) {
      const { content: contentRaw, size, totalPages, totalElements } = action.payload;
      // TODO:Remove following line in CAP-240
      const content = contentRaw.filter(item => RegExStrings.latinOnly.test(item.symbol));
      state.week52HighLow.totalElements = totalElements;
      state.week52HighLow.size = size;
      state.week52HighLow.totalPages = totalPages;
      state.week52HighLow.content = state.week52HighLow.content?.concat(...content);
      state.week52HighLow.numberOfElements = state.week52HighLow.content?.length;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLow, CallStatus.READY, state);
    },
    getWeek52HighLowFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLow, CallStatus.ERROR, state, status);
    },
    getWeek52HighLowTopList(state) {
      state.week52HighLowTopList.topNLow = [];
      state.week52HighLowTopList.topNHigh = [];
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLowTopList, CallStatus.PENDING, state);
    },
    getWeek52HighLowTopListCompleted(state, action: PayloadAction<Week52HighLowTopListResponse>) {
      const { payload: payloadRaw } = action;
      // TODO:Remove following 3 lines in CAP-240
      const topNLow = payloadRaw.topNLow.filter(item => RegExStrings.latinOnly.test(item.symbol));
      const topNHigh = payloadRaw.topNHigh.filter(item => RegExStrings.latinOnly.test(item.symbol));
      const payload = { ...payloadRaw, topNLow, topNHigh };
      state.week52HighLowTopList = payload;
      updateCompanyAndLogoCache(payload.topNLow, state);
      updateCompanyAndLogoCache(payload.topNHigh, state);
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLowTopList, CallStatus.READY, state);
    },
    getWeek52HighLowTopListFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLowTopList, CallStatus.ERROR, state, status);
    },
    getAllRatios(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData = DEFAULT_RATIOS_AND_FILTER_PARAMS;
      setFundamentalCallStatus(FundamentalCall.getAllRatios, CallStatus.PENDING, state);
    },
    getAllRatiosCompleted(state, action: PayloadAction<RatiosResponse>) {
      const { payload } = action;
      state.ratiosData = payload;
      setFundamentalCallStatus(FundamentalCall.getAllRatios, CallStatus.READY, state);
    },
    getAllRatiosFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getAllRatios, CallStatus.ERROR, state, status);
    },
    getRatiosValuation(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData.valuation = DEFAULT_RATIOS_AND_FILTER_PARAMS.valuation;
      setFundamentalCallStatus(FundamentalCall.getRatiosValuation, CallStatus.PENDING, state);
    },
    getRatiosValuationCompleted(state, action: PayloadAction<ValuationRatiosResponse>) {
      const { payload } = action;
      state.ratiosData.valuation = payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosValuation, CallStatus.READY, state);
    },
    getRatiosValuationFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosValuation, CallStatus.ERROR, state, status);
    },
    getRatiosProfitability(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData.profitability = DEFAULT_RATIOS_AND_FILTER_PARAMS.profitability;
      setFundamentalCallStatus(FundamentalCall.getRatiosProfitability, CallStatus.PENDING, state);
    },
    getRatiosProfitabilityCompleted(state, action: PayloadAction<ProfitabilityRatiosResponse>) {
      const { payload } = action;
      state.ratiosData.profitability = payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosProfitability, CallStatus.READY, state);
    },
    getRatiosProfitabilityFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosProfitability, CallStatus.ERROR, state, status);
    },
    getRatiosLiquidityAndSolvency(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData.liquidityAndSolvency = DEFAULT_RATIOS_AND_FILTER_PARAMS.liquidityAndSolvency;
      setFundamentalCallStatus(FundamentalCall.getRatiosLiquidityAndSolvency, CallStatus.PENDING, state);
    },
    getRatiosLiquidityAndSolvencyCompleted(state, action: PayloadAction<LiquidityAndSolvencyRatiosResponse>) {
      const { payload } = action;
      state.ratiosData.liquidityAndSolvency = payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosLiquidityAndSolvency, CallStatus.READY, state);
    },
    getRatiosLiquidityAndSolvencyFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosLiquidityAndSolvency, CallStatus.ERROR, state, status);
    },
    getGappers(state, action: PayloadAction<GapperParamType>) {
      const { page } = action.payload;
      if (page === 0) {
        state.gappersData = { ...DEFAULT_PAGE_RESPONSE };
      }
      state.gappersData.number = page ?? 0;
      setFundamentalCallStatus(FundamentalCall.getGappers, CallStatus.PENDING, state);
    },
    getGappersCompleted(state, action: PayloadAction<SimplePagedResponse<GapperResponse>>) {
      const { content, size, totalPages, totalElements } = action.payload;
      state.gappersData.totalElements = totalElements;
      state.gappersData.size = size;
      state.gappersData.totalPages = totalPages;
      state.gappersData.content = state.gappersData.content?.concat(...content);
      state.gappersData.numberOfElements = state.gappersData.content?.length;
      updateCompanyAndLogoCache(content, state);
      setFundamentalCallStatus(FundamentalCall.getGappers, CallStatus.READY, state);
    },
    getGappersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getGappers, CallStatus.ERROR, state, status);
    },
    getGappersCount(state, action: PayloadAction) {
      setFundamentalCallStatus(FundamentalCall.getGappersCount, CallStatus.PENDING, state);
    },
    getGappersCountCompleted(state, action: PayloadAction<GapperCountResponse>) {
      state.gappersData.totalElements = action.payload.data;
      setFundamentalCallStatus(FundamentalCall.getGappersCount, CallStatus.READY, state);
    },
    getGappersCountFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getGappersCount, CallStatus.ERROR, state, status);
    },
    getAvailableIndexes(state, action: PayloadAction) {
      setFundamentalCallStatus(FundamentalCall.getAvailableIndexes, CallStatus.PENDING, state);
    },
    getAvailableIndexesCompleted(state, action: PayloadAction<AvailableIndexesResponse[]>) {
      const availableIndexes: AvailableIndexesResponse[] = [];

      // Using sorted market data indexes list
      marketIndexesArray.forEach(marketIndex => {
        const foundedPayload = action.payload.find(payloadItem => payloadItem.asYahoo === marketIndex.symbol);
        if (foundedPayload) {
          availableIndexes.push({ ...foundedPayload, name: marketIndex.shownAs });// This line is to rename them in FE
        }
      });

      state.availableIndexes = availableIndexes;
      setFundamentalCallStatus(FundamentalCall.getAvailableIndexes, CallStatus.READY, state);
    },
    getAvailableIndexesFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getAvailableIndexes, CallStatus.ERROR, state, status);
    },
    setTopGainersAndLosersFilterValues(state, action: PayloadAction<TopGainerAndLoserPayload>) {
      state.topGainersAndLosersFilterValues = action.payload;
    },
    getMarketIndexes(state, action: PayloadAction) {
      setFundamentalCallStatus(FundamentalCall.getMarketIndexes, CallStatus.PENDING, state);
    },
    getMarketIndexesCompleted(state, action: PayloadAction<[]>) {
      setFundamentalCallStatus(FundamentalCall.getMarketIndexes, CallStatus.READY, state);
      state.marketIndexes = action.payload;
    },
    getMarketIndexesFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getMarketIndexes, CallStatus.ERROR, state, status);
    },
    getEpicMovers(state, action: PayloadAction<EpicMoversParamType>) {
      state.epicMoversData.responseData = DEFAULT_EPIC_MOVERS_DATA.responseData;
      state.epicMoversData.params = action.payload;
      setFundamentalCallStatus(FundamentalCall.getEpicMovers, CallStatus.PENDING, state);
    },
    getEpicMoversCompleted(state, action: PayloadAction<EpicMoversResponse>) {
      const { payload } = action;
      state.epicMoversData.responseData = payload;
      setFundamentalCallStatus(FundamentalCall.getEpicMovers, CallStatus.READY, state);
    },
    getEpicMoversFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getEpicMovers, CallStatus.ERROR, state, status);
    },
    /**
     * @deprecated  please use getEpicMovers instead. It will provide top gainers and top losers together in a single request
     */
    getTopGainers(state, action: PayloadAction<TopGainerAndLoserPayload>) {
      setFundamentalCallStatus(FundamentalCall.getTopGainers, CallStatus.PENDING, state);
    },
    /**
     * @deprecated  please use getEpicMovers instead. It will provide top gainers and top losers together in a single request
     */
    getTopGainersCompleted(state, action: PayloadAction<TopGainerAndLoserData[]>) {
      setFundamentalCallStatus(FundamentalCall.getTopGainers, CallStatus.READY, state);
      updateCompanyAndLogoCache(action.payload, state);
    },
    /**
     * @deprecated  please use getEpicMovers instead. It will provide top gainers and top losers together in a single request
     */
    getTopGainersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getTopGainers, CallStatus.ERROR, state, status);
    },
    /**
     * @deprecated  please use getEpicMovers instead. It will provide top gainers and top losers together in a single request
     */
    getTopLosers(state, action: PayloadAction<TopGainerAndLoserPayload>) {
      setFundamentalCallStatus(FundamentalCall.getTopLosers, CallStatus.PENDING, state);
    },
    /**
     * @deprecated  please use getEpicMovers instead. It will provide top gainers and top losers together in a single request
     */
    getTopLosersCompleted(state, action: PayloadAction<TopGainerAndLoserData[]>) {
      setFundamentalCallStatus(FundamentalCall.getTopLosers, CallStatus.READY, state);
      updateCompanyAndLogoCache(action.payload, state);
    },
    /**
     * @deprecated  please use getEpicMovers instead. It will provide top gainers and top losers together in a single request
     */
    getTopLosersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getTopLosers, CallStatus.ERROR, state, status);
    },
    getIPOs(state, action: PayloadAction<{ startDate: NullableString, endDate: NullableString }>) {
      setFundamentalCallStatus(FundamentalCall.getIPOs, CallStatus.PENDING, state);
    },
    getIPOsCompleted(state, action: PayloadAction<IPO[]>) {
      setFundamentalCallStatus(FundamentalCall.getIPOs, CallStatus.READY, state);
      state.IPOs = action.payload;
    },
    getIPOsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getIPOs, CallStatus.ERROR, state, status);
    },
    getSplits(state, action: PayloadAction<{
      symbols: string[],
      startDate: NullableString,
      endDate: NullableString,
    }>) {
      setFundamentalCallStatus(FundamentalCall.getSplits, CallStatus.PENDING, state);
    },
    getSplitsCompleted(state, action: PayloadAction<Split[]>) {
      state.splits = action.payload;
      setFundamentalCallStatus(FundamentalCall.getSplits, CallStatus.READY, state);
    },
    getSplitsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.ERROR, state, status);
    },
  },
  extraReducers: {
    [clearErrorsLog.type]: state => { state.errorsLog = []; },
  },
});

export const {
  clearStockSearch,
  searchStockByParams,
  searchStock,
  stockSearchFailed,
  stockSearchCompleted,
  stockSearchReset,
  loadStock,
  searchEarnings,
  searchEarningsCompleted,
  searchEarningsFailed,
  keyStatsFailed,
  keyStatsCompleted,
  searchCompany,
  searchCompanyCompleted,
  searchCompanyFailed,
  getCompany,
  getCompanyCompleted,
  getCompanyFailed,
  // TODO: Delete getCompanyLogo after merging ART-1918 and use getCompanyLogoAndName for both mobile and web
  getCompanyLogo,
  getCompanyLogoCompleted,
  getCompanyLogoFailed,
  getCompanyLogoAndName,
  getCompanyLogoAndNameCompleted,
  getCompanyLogoAndNameFailed,
  getPopularStocks,
  getPopularStocksCompleted,
  getPopularStocksFailed,
  getNewStocks,
  getNewStocksCompleted,
  getNewStocksFailed,
  getDividendPayers,
  getDividendPayersCompleted,
  getDividendPayersFailed,
  getHighGrowthCompanies,
  getHighGrowthCompaniesCompleted,
  getHighGrowthCompaniesFailed,
  getSectors,
  getSectorsCompleted,
  getSectorsFailed,
  getSectorDetail,
  getSectorDetailCompleted,
  getSectorDetailFailed,
  getETFs,
  getETFsCompleted,
  getETFsFailed,
  getETFDetails,
  getETFDetailsCompleted,
  getETFDetailsFailed,
  getStatements,
  getStatementsCompleted,
  getStatementsFailed,
  getStatementsCashFlow,
  getStatementsCashFlowCompleted,
  getStatementsCashFlowFailed,
  getStatementsBalanceSheet,
  getStatementsBalanceSheetCompleted,
  getStatementsBalanceSheetFailed,
  getStatementsIncome,
  getStatementsIncomeCompleted,
  getStatementsIncomeFailed,
  getWeek52HighLow,
  getWeek52HighLowCompleted,
  getWeek52HighLowFailed,
  getWeek52HighLowTopList,
  getWeek52HighLowTopListCompleted,
  getWeek52HighLowTopListFailed,
  getAllRatios,
  getAllRatiosCompleted,
  getAllRatiosFailed,
  getRatiosValuation,
  getRatiosValuationCompleted,
  getRatiosValuationFailed,
  getEpicMovers,
  getEpicMoversCompleted,
  getEpicMoversFailed,
  getRatiosProfitability,
  getRatiosProfitabilityCompleted,
  getRatiosProfitabilityFailed,
  getRatiosLiquidityAndSolvency,
  getRatiosLiquidityAndSolvencyCompleted,
  getRatiosLiquidityAndSolvencyFailed,
  getGappers,
  getGappersCompleted,
  getGappersFailed,
  getGappersCount,
  getGappersCountCompleted,
  getGappersCountFailed,
  getAvailableIndexes,
  getAvailableIndexesCompleted,
  getAvailableIndexesFailed,
  setTopGainersAndLosersFilterValues,
  getMarketIndexes,
  getMarketIndexesCompleted,
  getMarketIndexesFailed,
  getTopGainers,
  getTopGainersCompleted,
  getTopGainersFailed,
  getTopLosers,
  getTopLosersCompleted,
  getTopLosersFailed,
  getIPOs,
  getIPOsCompleted,
  getIPOsFailed,
  getSplits,
  getSplitsCompleted,
  getSplitsFailed,
} = fundamentalSlice.actions;

export default fundamentalSlice.reducer;
